import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { formatINR } from '@/lib/format';
import { dmy, getEditLog, type EditLogRow, type VoucherFull } from '@/api/accountsBooks';

const TONE = { Created: 'success', Altered: 'warning', Cancelled: 'danger' } as const;

/** Every voucher created, altered or cancelled — who, when, which version — as TallyPrime's Edit Log. */
export function EditLogPage() {
  const period = usePeriod('month');
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [open, setOpen] = useState<EditLogRow | null>(null);
  const range = period.range;
  const q = useQuery({
    queryKey: ['acc-books', 'edit-log', range?.from ?? '', range?.to ?? ''],
    queryFn: () => getEditLog({ from: range?.from, to: range?.to }),
  });
  const all = useMemo(() => q.data ?? [], [q.data]);
  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return all.filter((r) => (!action || r.action === action)
      && (!s || [r.snapshot.voucherNo, r.snapshot.type, r.by, ...r.snapshot.debit, ...r.snapshot.credit].some((x) => (x ?? '').toLowerCase().includes(s))));
  }, [all, search, action]);
  // The version before the one opened, to show what changed.
  const prev = open ? all.find((r) => r.voucherId === open.voucherId && r.version === open.version - 1) : undefined;

  return (
    <div>
      <SectionHeader title="Voucher Edit Log" />
      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <div className="relative w-full">
            <Search size={11} className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5" strokeWidth={2.25} />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" title="Voucher no., type, ledger or user" className="!pl-5 sm:!pl-8" />
          </div>
        </FilterField>
        <FilterField label="Action">
          <Select value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">All</option><option>Created</option><option>Altered</option><option>Cancelled</option>
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
      </FilterBar>

      <Table<EditLogRow>
        storageKey="acc-edit-log"
        title="Edit Log"
        count={`${rows.length} change${rows.length === 1 ? '' : 's'}`}
        rows={rows}
        rowKey={(r) => r.id}
        loading={q.isFetching && !q.data}
        empty="No voucher changes in this period."
        columns={[
          { key: 'at', header: 'Date & time', cell: (r) => new Date(r.at).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) },
          { key: 'action', header: 'Action', cell: (r) => <Badge tone={TONE[r.action]}>{r.action}</Badge> },
          { key: 'vch', header: 'Voucher', cell: (r) => <button type="button" onClick={() => setOpen(r)} className="font-mono font-semibold text-accent hover:underline">{r.snapshot.voucherNo}</button> },
          { key: 'type', header: 'Type', cell: (r) => r.snapshot.type },
          { key: 'vdate', header: 'Voucher date', cell: (r) => dmy(r.snapshot.date) },
          { key: 'ver', header: 'Version', align: 'right', cell: (r) => `v${r.version}` },
          { key: 'amt', header: 'Amount', align: 'right', cell: (r) => `₹${formatINR(r.snapshot.amount)}` },
          { key: 'by', header: 'By', cell: (r) => r.by ?? '—' },
        ]}
      />

      {open && (
        <Modal open onClose={() => setOpen(null)} size="lg" title={`${open.snapshot.type} ${open.snapshot.voucherNo} — v${open.version} ${open.action}`}>
          <div className={`grid gap-4 ${prev ? 'sm:grid-cols-2' : ''}`}>
            {prev && <Snapshot title={`Before (v${prev.version})`} v={prev.snapshot} />}
            <Snapshot title={prev ? `After (v${open.version})` : `v${open.version}`} v={open.snapshot} />
          </div>
        </Modal>
      )}
    </div>
  );
}

function Snapshot({ title, v }: { title: string; v: VoucherFull }) {
  return (
    <div className="rounded-lg border border-[var(--border-color)] p-3 text-xs">
      <div className="mb-2 font-bold text-[var(--text-primary)]">{title}</div>
      <div className="mb-2 text-[var(--text-secondary)]">
        {dmy(v.date)} · {v.status}{v.mode ? ` · ${v.mode}` : ''}{v.refNo ? ` · ${v.refNo}` : ''} · ₹{formatINR(v.amount)}
      </div>
      {v.lines.map((l) => (
        <div key={l.ledgerId} className="flex justify-between gap-2 border-t border-[var(--border-color)] py-1">
          <span>
            {l.ledger}
            {l.bills.map((b, i) => <span key={i} className="block text-[var(--text-muted)]">{b.refType}{b.billNo ? ` ${b.billNo}` : ''} · ₹{formatINR(b.amount)}</span>)}
          </span>
          <span className="whitespace-nowrap font-semibold">{l.side} ₹{formatINR(l.amount)}</span>
        </div>
      ))}
      {v.narration && <div className="mt-2 text-[var(--text-muted)]">{v.narration}</div>}
    </div>
  );
}
